import BackButton from "../shared/BackButton";
import FinalResultsTableMobile from "./FinalResultsTableMobile";
import { FinalResults } from "../../interfaces/ICompetitions";
import { prepareFinalResultsForDisplay, isRowDidNotPerformAllJudges } from "../../utils/roundHelpers";
import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";

interface FinalResultsTableProps {
  competitionName: string;
  categoryName: string;
  finalResults: FinalResults;
  onBack?: () => void;
}

export default function FinalResultsTable({
  competitionName,
  categoryName,
  finalResults,
  onBack,
}: FinalResultsTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sortedFinalResults = useMemo(() => prepareFinalResultsForDisplay(finalResults), [finalResults]);

  if (isMobile) {
    return (
      <FinalResultsTableMobile
        competitionName={competitionName}
        categoryName={categoryName}
        finalResults={sortedFinalResults}
        onBack={onBack}
      />
    );
  }

  return (
    <Paper>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            component="div"
            sx={{ color: "primary.main", fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 600 }}
          >
            Wyniki końcowe
          </Typography>
          <Typography variant="subtitle2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
            {categoryName}
          </Typography>
          <Typography variant="body2" component="div" color="text.secondary">
            {competitionName}
          </Typography>
        </Box>
        {onBack && <BackButton onClick={onBack} showTooltip={false} />}
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={3 + sortedFinalResults.rounds.length}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {sortedFinalResults.judges.map((judge) => (
                    <Chip
                      key={judge.id}
                      avatar={
                        <Avatar sx={{ width: 24, height: 24, fontSize: "0.75rem" }}>
                          {judge.index || `${judge.firstName?.[0] || ""}${judge.lastName?.[0] || ""}`}
                        </Avatar>
                      }
                      label={`${judge.firstName} ${judge.lastName}`}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ fontWeight: 600 }}>
                Skład sędziowski:
              </TableCell>
              {sortedFinalResults.rounds.map((round) => (
                <TableCell key={round.roundId} align="center">
                  {round.judges.join(" ")}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Miejsce
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Numer startowy
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Nazwa</TableCell>
              {sortedFinalResults.rounds.map((round) => (
                <TableCell key={round.roundId} align="center" sx={{ fontWeight: 600 }}>
                  {round.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFinalResults.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3 + sortedFinalResults.rounds.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Brak wyników końcowych
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedFinalResults.rows.map((row) => {
                const didNotPerform = isRowDidNotPerformAllJudges(row, sortedFinalResults.rounds);
                const strikeSx = didNotPerform
                  ? { textDecoration: "line-through", color: "text.secondary" }
                  : undefined;
                return (
                  <TableRow key={row.presentationId}>
                    <TableCell align="center">{didNotPerform ? "\u2014" : row.place}</TableCell>
                    <TableCell align="center" sx={strikeSx}>
                      {row.presentationId}
                    </TableCell>
                    <TableCell>
                      <Box component="span" sx={{ display: "inline" }}>
                        <Typography variant="body2" component="span" sx={strikeSx}>
                          {row.name}
                        </Typography>
                        {didNotPerform && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontStyle: "italic", ml: 0.5 }}
                          >
                            (nie wystąpił)
                          </Typography>
                        )}
                      </Box>
                      {row.title && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontStyle: "italic",
                            display: "block",
                            mt: 0.25,
                            ...(didNotPerform ? strikeSx : {}),
                          }}
                        >
                          {row.title}
                        </Typography>
                      )}
                    </TableCell>
                    {row.rounds.map((roundResult, index) => (
                      <TableCell key={`${row.presentationId}-${index}`} align="center">
                        {roundResult}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
